import { computed, inject, onScopeDispose, provide, readonly, ref, type ComputedRef, type InjectionKey, type Ref } from 'vue'

/** catalyst-vue 控制項尺寸。粗指標或窄視窗用 md（觸控尺寸），指標精確的桌機用 sm。 */
export type ControlSize = 'sm' | 'md'
/** IconButton 的尺寸刻度，與 ControlSize 不是一對一。 */
export type IconControlSize = 'xs' | 'sm' | 'md'

export function isCompactLibrary(width: number, coarse: boolean): boolean {
  return width < 1280 || (coarse && width <= 1366)
}

export function controlSizeFor(compact: boolean): ControlSize {
  return compact ? 'md' : 'sm'
}

/**
 * 與同一列文字按鈕等高的 IconButton 尺寸。
 *
 * catalyst-vue 的兩套刻度在 sm 這一階對不上：Button 的高度由行高與 padding 決定
 * （sm 為 28px），IconButton 則是固定方框（sm 為 size-8 也就是 32px）。xs 與 md
 * 兩階剛好一致。因此同一列要等高時，sm 的文字按鈕要搭配 xs 的圖示按鈕。
 */
export function iconControlSizeFor(size: ControlSize): IconControlSize {
  return size === 'sm' ? 'xs' : 'md'
}

const layoutKey: InjectionKey<Readonly<Ref<boolean>>> = Symbol('library-layout')

export function provideLibraryLayout(): Readonly<Ref<boolean>> {
  const pointer = window.matchMedia('(pointer: coarse)')
  const compact = ref(isCompactLibrary(window.innerWidth, pointer.matches))
  const update = () => { compact.value = isCompactLibrary(window.innerWidth, pointer.matches) }
  window.addEventListener('resize', update)
  pointer.addEventListener('change', update)
  onScopeDispose(() => {
    window.removeEventListener('resize', update)
    pointer.removeEventListener('change', update)
  })
  provide(layoutKey, readonly(compact))
  return readonly(compact)
}

export function useLibraryLayout(): Readonly<Ref<boolean>> {
  const compact = inject(layoutKey)
  if (!compact) throw new Error('書庫元件需位於 provideLibraryLayout 範圍內')
  return compact
}

/** 控制項尺寸一律由版面推導，不另立第二套斷點。 */
export function useControlSize(): ComputedRef<ControlSize> {
  const compact = useLibraryLayout()
  return computed(() => controlSizeFor(compact.value))
}

/** 與 useControlSize() 同一列、等高的圖示按鈕尺寸。 */
export function useIconControlSize(): ComputedRef<IconControlSize> {
  const size = useControlSize()
  return computed(() => iconControlSizeFor(size.value))
}
